export type BillSplitterMode = 'page' | 'chat';
export type BillSourceType = 'image' | 'pdf' | 'document' | 'text' | 'chat' | 'manual';
export type ChargeAllocationStrategy = 'proportional' | 'equal' | 'specific-person';

export interface BillItemAllocation { personId: string; share: number; }
export interface BillPerson { id: string; name: string; }
export interface BillItem {
  id: string;
  name: string;
  quantity: number;
  /** Integer minor units (cents). */
  unitPrice?: number;
  /** Integer minor units (cents), before discounts. */
  total: number;
  /** Discount per unit, in minor units; multiplied by quantity locally. */
  unitDiscount?: number;
  /** Additional discount for this entire line, in minor units. */
  discount?: number;
  allocations: BillItemAllocation[];
  confidence?: number;
}
export interface BillCharge {
  amount: number;
  strategy: ChargeAllocationStrategy;
  personId?: string;
  /** Optional percentage of the net item subtotal, used for bill discounts. */
  percentage?: number;
}
export interface BillSource { type: BillSourceType; fileName?: string; mime?: string; storagePath?: string; }
export interface Bill {
  id: string;
  merchant?: string;
  date?: string;
  currency: string;
  items: BillItem[];
  subtotal?: number;
  tax: BillCharge;
  serviceCharge: BillCharge;
  tip: BillCharge;
  discount: BillCharge;
  /** Receipt's stated total, in minor units. */
  statedTotal?: number;
  people: BillPerson[];
  source?: BillSource;
  extractionWarnings?: string[];
  /** Prevents a chat's original receipt from being imported again after reset. */
  sourceProcessed?: boolean;
}
export interface ExtractedBill {
  merchant?: string; date?: string; currency?: string;
  items: Array<{ name: string; quantity?: number; unitPrice?: number; total?: number; totalIncludesDiscount?: boolean; unitDiscount?: number; discount?: number; discountPercent?: number; confidence?: number; assignedTo?: string[] }>;
  subtotal?: number; tax?: number; serviceCharge?: number; tip?: number; discount?: number; discountPercent?: number; statedTotal?: number;
}
export interface BillValidationResult { valid: boolean; warnings: string[]; itemSubtotal: number; calculatedTotal: number; difference: number; }
export interface BillSplitterResult { billId: string; currency: string; people: Array<{ id: string; name: string; total: number }>; total: number; }
export type BillSplitterOperation =
  | { type: 'assign_item'; itemId: string; personIds: string[] }
  | { type: 'assign_item_custom'; itemId: string; allocations: BillItemAllocation[] }
  | { type: 'add_person'; name: string }
  | { type: 'remove_person'; personId: string }
  | { type: 'rename_person'; personId: string; name: string }
  | { type: 'add_item'; item: Partial<BillItem> }
  | { type: 'update_item'; itemId: string; changes: Partial<BillItem> }
  | { type: 'remove_item'; itemId: string };
export interface BillSplitterToolInput { source?: { type: 'uploaded_file' | 'text' | 'chat_message'; text?: string; attachmentIds?: string[] }; people?: string[]; instructions?: string; }
export interface BillSplitterUpdateInput { billId: string; operations: BillSplitterOperation[]; }

export const emptyCharge = (): BillCharge => ({ amount: 0, strategy: 'proportional' });
export const newId = (): string => globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
